import React from 'react';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { createEnumOptions } from '@/utils/core/data-transformation';
import { getEnumTranslationKey } from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createTableActionsColumn,
  formatChatThreadLabel,
  formatTZLocal,
  getEnumNamespace,
} from '@/utils/infrastructure';
import { MailEventUtils } from '@/utils/infrastructure/constants';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { registerModuleFilters } from '@/utils/input/filters';

import { MODEL_NAME } from './constants';

const { BaseFilters, UserFilter } = Filters;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CreatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.createdDate',
};

const EmailMessageTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'messageType',
  label: 'global.type',
  enumNamespace: getEnumNamespace(CoreEnums.EmailMessageType),
  enumSource: CoreEnums.EmailMessageType,
  options: createEnumOptions(CoreEnums.EmailMessageType, 'global.emailType', {
    casing: 'camel',
  }),
  filterSearch: true,
};

const EmailUserFilter = {
  ...UserFilter,
  filterSearch: true,
};

const ProcessInstanceFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'processInstanceId',
  label: 'global.text.application',
  knownType: CoreEnums.FormFieldType.InternalEntity,
  apiRoute: 'processInstance/requests/0',
  labelInValue: true,
  filterSearch: true,
  params: {
    select: 'id,name',
    orderBy: 'name asc',
  },
};

const AgentFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'agentId',
  label: 'global.text.agent',
  apiRoute: 'ai/agent/allowed',
  filterSearch: true,
  params: {
    select: 'id,name',
    filter: 'isActive eq true',
    orderBy: 'name asc',
  },
};

const ChatThreadFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'chatThreadId',
  label: 'global.email.chatThread',
  apiRoute: 'chat/thread/paged',
  uuidRouteFragments: ['agentId'],
  uuidRouteFragmentsOptional: true,
  filterSearch: true,
  params: {
    select: 'id,number,name',
    orderBy: 'number asc',
  },
  nomenclatureMapRemoteData: (data) =>
    (data || []).map((item) => ({
      ...item,
      label: formatChatThreadLabel(item) || item.name || item.label,
    })),
};

export const filtersConfig = [
  CreatedDateFilter,
  EmailMessageTypeFilter,
  EmailUserFilter,
  ProcessInstanceFilter,
  AgentFilter,
  ChatThreadFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

export function isEmailSearch(value) {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.length > 0 && EMAIL_REGEX.test(trimmed);
}

/**
 * Legacy behavior: when the toolbar search is an email, exact-match `to`
 * and drop free-text search / searchSelect (filters from payload are replaced).
 */
export function applyExactEmailToFilter(params = {}) {
  const search = typeof params.search === 'string' ? params.search.trim() : params.search;
  if (!isEmailSearch(search)) return params;

  const escapedEmail = String(search).replace(/'/g, "''");
  const { search: _search, searchSelect: _ss, notSelectSearch: _nss, ...rest } = params;
  return {
    ...rest,
    filter: `to eq '${escapedEmail}'`,
  };
}

export function mapEmailMessageTraceDetailFields(rows, { item, formatMessage }) {
  if (!item || !rows?.length) return rows;

  return rows.map((row) => {
    if (row.name === 'responseCode') {
      const status = MailEventUtils.getStatusByResponseCode(item.responseCode);
      return {
        ...row,
        type: 'badge',
        badgeType: 'process',
        badgeStatus: status.color,
        value: MailEventUtils.formatStatusLabel(item.responseCode, formatMessage),
        locale: false,
      };
    }

    if (row.name === 'chatThread') {
      const label = formatChatThreadLabel(item.chatThread);
      if (!label) return row;
      return { ...row, value: label, locale: false };
    }

    if (row.name !== 'user') return row;

    const user = item.user;
    const fullName = user?.fullName ?? row.value;
    const id = user?.id;
    if (!fullName && !id) return row;

    return {
      ...row,
      viewComponent: 'user',
      value: fullName,
      viewProps: { id, fullName },
    };
  });
}

export function buildEmailMessageTraceColumns({
  formatMessage,
  openDrawerCreateOrEdit,
  searchCriteria,
}) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'created',
      title: formatMessage({ id: 'global.created' }),
      dataIndex: 'created',
      sorter: true,
      render: (value) => (value ? formatTZLocal(value) : null),
    },
    {
      key: 'responseCode',
      title: formatMessage({ id: 'global.text.status' }),
      dataIndex: 'responseCode',
      sorter: true,
      align: 'center',
      render: (responseCode) => {
        const status = MailEventUtils.getStatusByResponseCode(responseCode);
        return (
          <Badge
            status={status.color}
            type="process"
            text={formatMessage({ id: status.statusText })}
          />
        );
      },
    },
    {
      key: 'messageType',
      title: formatMessage({ id: 'global.type' }),
      dataIndex: 'messageType',
      sorter: true,
      align: 'center',
      render: (value) => {
        if (value === undefined || value === null) {
          return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
        }
        const translationKey = getEnumTranslationKey(
          CoreEnums.EmailMessageType,
          value,
          'global.emailType',
        );
        return (
          <Badge
            status="default"
            type="process"
            text={formatMessage({ id: translationKey }) || DEFAULT_EMPTY_VALUE}
          />
        );
      },
    },
    {
      key: 'to',
      title: formatMessage({ id: 'global.email.to' }),
      dataIndex: 'to',
      sorter: true,
      render: highlight,
    },
    {
      key: 'subject',
      title: formatMessage({ id: 'global.email.subject' }),
      dataIndex: 'subject',
      sorter: true,
      textWrap: 'word-break',
      render: highlight,
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (el) => (
        <TableRowButtons
          onDetail={() => openDrawerCreateOrEdit(el.id, true)}
          formatMessage={formatMessage}
          actionOrder={{ primary: ['detail'], secondary: [] }}
        />
      ),
    }),
  ];
}
